import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, LogOut, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

const DEFAULT_PW = "Empro123";

export default function ForcePinChange() {
  const { user, changePassword, logout } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async () => {
    if (pw.length < 6) return toast.error("Hasło musi mieć co najmniej 6 znaków");
    if (!/[A-Za-zÀ-ž]/.test(pw) || !/\d/.test(pw)) return toast.error("Hasło musi zawierać litery i co najmniej jedną cyfrę");
    if (pw === DEFAULT_PW) return toast.error(`Wybierz hasło inne niż domyślne „${DEFAULT_PW}”`);
    if (pw !== confirm) return toast.error("Hasła nie są takie same");
    setBusy(true);
    try {
      await changePassword(pw);
      toast.success("Hasło zmienione. Miłej pracy!");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const field = (val, set, id, label) => (
    <div>
      <label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{label}</label>
      <div className="relative mt-1">
        <input data-testid={id} type={show ? "text" : "password"} autoComplete="new-password" value={val}
          onChange={(e) => set(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="h-12 w-full rounded-xl border border-border bg-background px-4 pr-11 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <button type="button" data-testid={`${id}-toggle`} onClick={() => setShow((v) => !v)} aria-label={show ? "Ukryj hasło" : "Pokaż hasło"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-amber-400 transition-colors">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen relative z-10 grid place-items-center px-4">
      <div data-testid="force-password-change" className="w-full max-w-sm rounded-2xl border border-border bg-card p-7 space-y-5 rise">
        <div className="flex items-center justify-between">
          <Logo />
          <Button data-testid="force-password-logout" variant="ghost" size="sm" onClick={logout} className="gap-2"><LogOut className="h-4 w-4" />Wyloguj</Button>
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-400/10 px-4 py-3">
          <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0" />
          <p className="text-sm font-semibold">Witaj, {user.name}. Ustaw swoje prywatne hasło, aby kontynuować.</p>
        </div>
        <p className="text-xs text-muted-foreground">Hasło: min. 6 znaków, litery i co najmniej jedna cyfra. Bez wymogu znaków specjalnych.</p>
        {field(pw, setPw, "new-password-input", "Nowe hasło")}
        {field(confirm, setConfirm, "confirm-password-input", "Powtórz hasło")}
        <Button data-testid="submit-new-password" onClick={submit} disabled={busy} className="w-full h-12 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold uppercase tracking-wider">
          {busy ? "Zapisywanie…" : "Ustaw hasło i wejdź"}
        </Button>
      </div>
    </div>
  );
}
